import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { Space, Spin } from 'antd';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  findFrequencyByBenefitKind,
  getAgeBandedPlans,
} from 'modules/benefitsWellness/util';
import MDVPlansWrapper from 'modules/benefitsWellness/components/MDVPlansWrapper/MDVPlansWrapper';
import AddOrRemovePlans from 'modules/benefitsWellness/components/AddOrRemovePlans/AddOrRemovePlans';
import TableContextProvider from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import {
  getVisionPlanList,
  saveAndReloadVisionSection,
} from 'modules/benefitsWellness/slices/visionSlice';
import useBenguide from 'hooks/benguide';
import MDVPlan from 'model/MDVPlan';
import { BENEFIT_CATEGORY } from 'constants/commonConstants';
import { VISION_PLAN_CHANNEL } from 'modules/clients/UPClient/UPClientConst';
import VisionPlansChannel from 'modules/clients/UPClient/channels/VisionPlansChannel';
import NoMDVPlansView from 'components/NoPlansView/NoMDVPlansView';
import VisionCardWrapper from 'modules/benefitsWellness/components/VisionCardWrapper/VisionCardWrapper';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import {
  AGE_BAND,
  FOUR_TIER,
  N_TIER,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import Rate from 'model/Rate';

import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import BenefitGuideNotificationsChannel from 'modules/clients/UPClient/channels/BenefitGuideNotificationsChannel';
import { NotificationContextProvider } from 'context/NotificationContext';
import FooterTextSection from 'components/FooterTextSection';
import { checkBenefitGuidesRates } from 'modules/benefitsWellness/slices/benefitWellnessSlice';
import styles from './vision.module.less';

const Vision: FC = () => {
  const { visionPlanList, visionSection } = useAppSelector(
    (state) => state.vision
  );
  const { rateValidations, rateValidationInProgress } = useAppSelector(
    (state) => state.benefitWellness
  );
  const { plans } = visionSection;
  const dispatch = useAppDispatch();
  const benguide = useBenguide();
  const [planListUpdateFlag, setPlanListUpdateFlag] = useState<boolean>(false);
  const { masterId, planYearId, employerId, benefitClasses } = benguide;
  const isEditMode = benguide.isEditMode;
  const updatePlanList = (
    visionPlanList: MDVPlan[],
    orderByPlanNames: boolean
  ) => {
    setPlanListUpdateFlag(!planListUpdateFlag);
    dispatch(
      saveAndReloadVisionSection(masterId, visionPlanList, orderByPlanNames)
    );
  };
  const [isSaveDisable, setSaveDisable] = useState<boolean>(false);
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);

  useEffect(() => {
    dispatch(
      checkBenefitGuidesRates(benguide.masterId, MDVTypes.VISION.toUpperCase())
    );
  }, [benguide.masterId, dispatch]);

  const enablePlanRecommender = plans.length > 1;
  const contribFreq = findFrequencyByBenefitKind(
    benguide.contributionFrequencies,
    BENEFIT_CATEGORY.VISION.value
  );
  const { enabled } = contribFreq;

  const reloadAllVisionPlans = useCallback(() => {
    dispatch(getVisionPlanList(employerId, planYearId, benefitClasses));
  }, [employerId, planYearId, benefitClasses, dispatch]);

  const showAlert = () => {
    setIsAlertVisible(true);
  };

  const getAlert = () => (
    <AlertMessage
      className={styles.alert}
      type="success"
      message={'Successfully created new plan.'}
      closeAlert={() => setIsAlertVisible(false)}
      wrapperClassName={styles.planAlertWrapper}
    />
  );

  const foundRates =
    (!isEmpty(plans) &&
      plans
        .map((plan) => plan.rates)
        .filter((plan) => plan)
        .map((rate) => {
          return Object.values(rate);
        })
        .reduce((arr1, arr2) => {
          return arr1.concat(arr2);
        }, [])
        .map((rate) => (rate as Rate).type)) ||
    [];

  const nTierCounts = useMemo(
    () =>
      plans.reduce((nTireMax: number, plan: MDVPlan) => {
        // Handle case where plan.rates is null or undefined
        const rates = plan.rates ? Object.values(plan.rates) : [];
        const contributionsCount = rates.map((rate: any) => {
          return rate?.ntierContributions?.contributions
            ? rate.ntierContributions.contributions.length
            : 0;
        });
        const planMax = Math.max(0, ...contributionsCount);
        return Math.max(nTireMax, planMax);
      }, 0),
    [plans]
  );

  const rateType = foundRates.includes(N_TIER)
    ? N_TIER
    : foundRates.includes(FOUR_TIER)
    ? FOUR_TIER
    : AGE_BAND;

  return (
    <NotificationContextProvider isDisableSave={isSaveDisable}>
      {isAlertVisible && getAlert()}
      <div className={styles.visionWrapper}>
        <VisionPlansChannel />
        <BenefitGuideRefreshChannel benefitKind={MDVTypes.VISION} />
        <BenefitGuideNotificationsChannel
          setDisableSave={() => setSaveDisable(true)}
        />
        <MDVPlansWrapper
          planType={MDVTypes.VISION}
          addedPlansCount={plans.length}
          enablePlanRecommender={enablePlanRecommender}
          showPlanRecommender={false}
          disablePlanRecommenderOnEdit={!(plans.length > 1)}
          hasMismatchContributions={
            rateValidations.hasMismatchContributions ||
            !enabled ||
            getAgeBandedPlans(plans).length === plans.length
          }
          rateType={rateType}
          planListUpdateFlag={planListUpdateFlag}
          nTiersCount={
            foundRates.includes(N_TIER) &&
            foundRates.includes(FOUR_TIER) &&
            nTierCounts < 4
              ? 4
              : nTierCounts
          }
        />
        {plans.length > 0 ? (
          rateValidationInProgress ? (
            <Space size="middle">
              <Spin size="large" />
            </Space>
          ) : (
            <TableContextProvider>
              <VisionCardWrapper
                onUpdatePlanList={updatePlanList}
                allPlans={visionPlanList.data}
                reloadPlans={reloadAllVisionPlans}
                showAlert={showAlert}
                isLoading={visionPlanList.inProgress}
              />
            </TableContextProvider>
          )
        ) : isEditMode ? (
          <AddOrRemovePlans
            visible={true}
            withoutModal={true}
            title="Vision"
            onSave={updatePlanList}
            addedPlanList={plans}
            allPlans={visionPlanList.data}
            reloadPlans={reloadAllVisionPlans}
            isLoading={visionPlanList.inProgress}
            channel={VISION_PLAN_CHANNEL}
            showAlert={showAlert}
          />
        ) : (
          <NoMDVPlansView />
        )}
        {plans.length > 0 && (
          <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
        )}
      </div>
    </NotificationContextProvider>
  );
};

export default Vision;
